import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type { FieldDefinition, Group, PersonDetail, Role } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { localized } from '../../shared/format';
import { useSession } from '../../shared/session';
import {
  AdminIcon,
  Btn,
  DefinitionList,
  Drawer,
  Empty,
  Field,
  Input,
  Row,
  Sect,
  Segmented,
  Select,
  TabPanel,
  Tabs,
} from '../admin-kit';
import { PersonAccountSection, PersonExitSection } from './person-access-section';
import { PersonManagerSection } from './person-manager-section';
import { useMelding } from './people-ui';

export type PersonTab = 'details' | 'fields' | 'groups' | 'access';

const TALEN = [
  { value: 'nl' as const, label: 'Nederlands' },
  { value: 'fr' as const, label: 'Français' },
  { value: 'en' as const, label: 'English' },
];

/**
 * M2 — het paneel van een persoon: rechts, 560 breed, de lijst blijft erachter staan.
 *
 * Tabs Gegevens · Gegevensvelden · Groepen · Toegang. Tot BHR-2 stonden die vier onder elkaar in
 * een kaart naast de lijst; de handelingen en hun controles zijn dezelfde gebleven.
 */
export function PersonDrawer({
  personId,
  initialTab = 'details',
  groups,
  fields,
  onClose,
  onSaved,
}: {
  personId: string;
  initialTab?: PersonTab;
  groups: Group[];
  fields: FieldDefinition[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const melding = useMelding();
  const [tab, setTab] = useState<PersonTab>(initialTab);

  const person = useQuery({
    queryKey: ['directory', 'person', personId],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/people/{personId}', {
        params: { path: { personId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as PersonDetail;
    },
  });

  const save = useMutation({
    mutationFn: async (changes: Record<string, unknown>) => {
      const { error } = await api.PATCH('/api/v1/people/{personId}', {
        params: { path: { personId } },
        body: changes as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      melding.ok(t('common.saved'));
      await person.refetch();
      onSaved();
    },
    onError: () => melding.fout(t('people.saveFailed')),
  });

  const detail = person.data ?? null;

  /*
    Gecontroleerde velden met een eigen staat, en niet `defaultValue` + `onBlur`.

    Met `defaultValue` bepaalt de **eerste** render wat er in het veld staat; elke latere wijziging
    van `detail` gaat eraan voorbij. De sleutel op het paneel lost dat op bij het wisselen van
    persoon, maar niet bij een verversing van dezelfde persoon - en dan toont het scherm nog de oude
    waarde nadat de server de nieuwe al heeft.

    `useEffect` op `detail` en niet op `personId`: het paneel moet ook bijdraaien als de query
    opnieuw ophaalt. Wat de gebruiker intussen typte wordt daarbij overschreven, en dat is juist:
    een opgeslagen waarde van de server is de waarheid.

    De staat staat hier en niet in de tab, zodat wisselen van tab niets verliest.
  */
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    employeeNumber: '',
    email: '',
  });
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fouten, setFouten] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!detail) return;
    setForm({
      firstName: detail.firstName,
      lastName: detail.lastName,
      employeeNumber: detail.employeeNumber ?? '',
      email: detail.email ?? '',
    });
    setFieldValues(detail.fieldValues as Record<string, string>);
    setFouten({});
  }, [detail]);

  /**
   * Bewaart een veld, maar alleen als het mag.
   *
   * Voornaam en achternaam zijn verplicht: een leeg verplicht veld wordt niet verstuurd, want de
   * API zou het weigeren en de gebruiker zag alleen een rode melding zonder te weten welk veld.
   * Hier staat de fout bij het veld zelf.
   */
  const bewaarVeld = (
    sleutel: 'firstName' | 'lastName' | 'employeeNumber' | 'email',
    waarde: string,
  ) => {
    const huidig =
      sleutel === 'employeeNumber'
        ? (detail?.employeeNumber ?? '')
        : sleutel === 'email'
          ? (detail?.email ?? '')
          : (detail?.[sleutel] ?? '');
    if (waarde === huidig) return;

    if ((sleutel === 'firstName' || sleutel === 'lastName') && waarde.trim() === '') {
      setFouten((vorige) => ({ ...vorige, [sleutel]: t('people.required') }));
      return;
    }
    setFouten((vorige) => {
      const rest = { ...vorige };
      delete rest[sleutel];
      return rest;
    });

    save.mutate(
      sleutel === 'firstName' || sleutel === 'lastName'
        ? { [sleutel]: waarde }
        : { [sleutel]: waarde || null },
    );
  };

  const groepNamen = detail
    ? groups.filter((group) => detail.groupIds.includes(group.id)).map((group) => group.name)
    : [];
  // "1021 · Productie, Ploeg B": het nummer en de groepen. Een functie kent een persoon niet; die
  // staat, als het bedrijf ze bijhoudt, in een gegevensveld.
  const sub = detail
    ? [detail.employeeNumber, groepNamen.join(', ')].filter(Boolean).join(' · ') || undefined
    : undefined;

  return (
    <Drawer
      open
      onClose={onClose}
      title={detail ? `${detail.firstName} ${detail.lastName}` : t('common.loading')}
      sub={sub}
      testId="person-drawer"
      footer={
        <Btn variant="sec" onClick={onClose}>
          {t('common.close')}
        </Btn>
      }
    >
      {person.isPending ? (
        <LoadingState rows={5} label={t('common.loading')} />
      ) : !detail ? (
        <Empty icon={<AdminIcon name="people" size={24} />} title={t('errors.generic')} />
      ) : (
        <>
          <Tabs
            label={t('beheer.medewerkers.tabsLabel')}
            idPrefix={`persoon-${personId}`}
            value={tab}
            onChange={setTab}
            tabs={[
              { key: 'details', label: t('people.tabDetails') },
              { key: 'fields', label: t('beheer.medewerkers.tabFields') },
              { key: 'groups', label: t('people.tabGroups'), count: detail.groupIds.length },
              { key: 'access', label: t('access.title') },
            ]}
          />

          {tab === 'details' ? (
            <TabPanel idPrefix={`persoon-${personId}`} tabKey="details" className="a-stack">
              <Row>
                <Field label={t('people.firstName')} error={fouten.firstName}>
                  {(props) => (
                    <Input
                      {...props}
                      data-testid="person-first-name"
                      value={form.firstName}
                      onChange={(event) => setForm({ ...form, firstName: event.target.value })}
                      onBlur={(event) => bewaarVeld('firstName', event.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('people.lastName')} error={fouten.lastName}>
                  {(props) => (
                    <Input
                      {...props}
                      data-testid="person-last-name"
                      value={form.lastName}
                      onChange={(event) => setForm({ ...form, lastName: event.target.value })}
                      onBlur={(event) => bewaarVeld('lastName', event.target.value)}
                    />
                  )}
                </Field>
              </Row>
              <Field label={t('people.employeeNumber')}>
                {(props) => (
                  <Input
                    {...props}
                    data-testid="person-employee-number"
                    value={form.employeeNumber}
                    onChange={(event) => setForm({ ...form, employeeNumber: event.target.value })}
                    onBlur={(event) => bewaarVeld('employeeNumber', event.target.value)}
                  />
                )}
              </Field>
              <Field label={t('people.email')}>
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    data-testid="person-email"
                    value={form.email}
                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                    onBlur={(event) => bewaarVeld('email', event.target.value)}
                  />
                )}
              </Field>
              <div className="a-field">
                <div className="a-label" id={`taal-${personId}`}>
                  {t('people.language')}
                </div>
                <Segmented
                  labelledBy={`taal-${personId}`}
                  options={TALEN}
                  value={detail.preferredLanguage}
                  disabled={save.isPending}
                  onChange={(taal) => save.mutate({ preferredLanguage: taal })}
                />
              </div>
              {/*
                De status staat er als **feit** en niet als keuzelijst.

                Iemand uit dienst zetten is onomkeerbaar genoeg om een bevestiging te verdienen, en
                die bestaat al: "Uit dienst" in de tab Toegang, met de datum en de reden erbij. Een
                tweede weg via een keuzelijst zou dezelfde handeling zonder vraag en zonder reden
                uitvoeren, en dan is de bevestiging een formaliteit die je kunt omzeilen.
              */}
              <DefinitionList
                items={[
                  [
                    t('people.status'),
                    <span key="status" data-testid="person-status">
                      {detail.status === 'active' ? t('people.active') : t('people.deactivated')}
                    </span>,
                  ],
                ]}
              />
            </TabPanel>
          ) : null}

          {tab === 'fields' ? (
            <TabPanel idPrefix={`persoon-${personId}`} tabKey="fields" className="a-stack">
              {fields.length === 0 ? (
                <Empty
                  icon={<AdminIcon name="tag" size={24} />}
                  title={t('fields.empty')}
                  text={t('fields.emptyHint')}
                />
              ) : (
                fields.map((definition) => (
                  <Field key={definition.id} label={localized(definition.label) ?? definition.key}>
                    {(props) =>
                      definition.type === 'select' ? (
                        <Select
                          {...props}
                          value={fieldValues[definition.key] ?? ''}
                          onChange={(event) =>
                            save.mutate({
                              fieldValues: { [definition.key]: event.target.value || null },
                            })
                          }
                        >
                          <option value="">-</option>
                          {(definition.options ?? []).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          {...props}
                          /*
                            Het invoertype volgt het type van het veld (punt 11). Een geboortedatum
                            in een gewoon tekstveld levert "12/3" op bij de ene beheerder en
                            "2026-03-12" bij de andere, en dan staat er in een kolom twee keer
                            iets anders.
                          */
                          type={
                            definition.type === 'date'
                              ? 'date'
                              : definition.type === 'number'
                                ? 'number'
                                : 'text'
                          }
                          value={fieldValues[definition.key] ?? ''}
                          onChange={(event) =>
                            setFieldValues({ ...fieldValues, [definition.key]: event.target.value })
                          }
                          onBlur={(event) =>
                            event.target.value !== (detail.fieldValues[definition.key] ?? '') &&
                            save.mutate({
                              fieldValues: { [definition.key]: event.target.value || null },
                            })
                          }
                        />
                      )
                    }
                  </Field>
                ))
              )}
            </TabPanel>
          ) : null}

          {tab === 'groups' ? (
            <TabPanel idPrefix={`persoon-${personId}`} tabKey="groups" className="a-stack">
              {groups.length === 0 ? (
                <Empty
                  icon={<AdminIcon name="groups" size={24} />}
                  title={t('groups.empty')}
                  text={t('groups.emptyHint')}
                />
              ) : (
                <>
                  <div className="a-hint">{t('beheer.medewerkers.groupsHint')}</div>
                  <div className="a-pills">
                    {groups.map((group) => {
                      const isMember = detail.groupIds.includes(group.id);
                      return (
                        <Btn
                          key={group.id}
                          size="sm"
                          variant={isMember ? 'primary' : 'sec'}
                          aria-pressed={isMember}
                          icon={isMember ? <AdminIcon name="check" size={14} /> : undefined}
                          disabled={save.isPending}
                          onClick={() =>
                            save.mutate({
                              groupIds: isMember
                                ? detail.groupIds.filter((id) => id !== group.id)
                                : [...detail.groupIds, group.id],
                            })
                          }
                        >
                          {group.name}
                        </Btn>
                      );
                    })}
                  </div>
                </>
              )}
            </TabPanel>
          ) : null}

          {tab === 'access' ? (
            <TabPanel idPrefix={`persoon-${personId}`} tabKey="access" className="a-stack">
              <PersonAccountSection
                personId={personId}
                employeeNumber={detail.employeeNumber}
                onChanged={onSaved}
              />
              <PersonRolesSection personId={personId} />
              <PersonManagerSection personId={personId} />
              <PersonExitSection
                personId={personId}
                temporary={detail.personType !== 'employee'}
                endDate={detail.plannedExitDate}
                onChanged={onSaved}
              />
            </TabPanel>
          ) : null}
        </>
      )}
      {melding.element}
    </Drawer>
  );
}

/**
 * De rollen van deze medewerker. Staat hier en niet op het rollenscherm, omdat de vraag
 * meestal andersom komt: je kijkt naar iemand en wilt weten wat hij mag. Niet in M2; in de tab
 * Toegang, omdat een rol zegt wat iemand met zijn account mag.
 *
 * Zonder het recht om rollen te zien blijft dit blok weg — dan is er niets te tonen.
 */
function PersonRolesSection({ personId }: { personId: string }) {
  const { t, i18n } = useTranslation();
  const { api, has, refreshPermissions } = useSession();
  const melding = useMelding();
  const language = i18n.language.slice(0, 2);
  const canView = has('access.role.view');
  const canAssign = has('access.role.assign');

  const roles = useQuery({
    queryKey: ['access', 'roles'],
    enabled: canView,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/roles');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { roles: Role[] }).roles;
    },
  });

  const assigned = useQuery({
    queryKey: ['access', 'person-roles', personId],
    enabled: canView,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/people/{personId}/roles', {
        params: { path: { personId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { roleIds: string[] }).roleIds;
    },
  });

  const save = useMutation({
    mutationFn: async (roleIds: string[]) => {
      const { error } = await api.PUT('/api/v1/people/{personId}/roles', {
        params: { path: { personId } },
        body: { roleIds } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      melding.ok(t('common.saved'));
      await assigned.refetch();
      await refreshPermissions();
    },
    onError: (error) =>
      melding.fout(
        problemCode(error) === 'LAST_ADMIN' ? t('roles.lastAdmin') : t('roles.assignFailed'),
      ),
  });

  if (!canView) return null;

  const current = assigned.data ?? [];

  return (
    <Sect title={t('roles.title')}>
      <div className="a-pills">
        {(roles.data ?? []).map((role) => {
          const hasRole = current.includes(role.id);
          const name =
            (role.name as Record<string, string | undefined>)[language] ?? role.name.nl ?? '—';
          return (
            <Btn
              key={role.id}
              size="sm"
              variant={hasRole ? 'primary' : 'sec'}
              aria-pressed={hasRole}
              icon={hasRole ? <AdminIcon name="check" size={14} /> : undefined}
              disabled={!canAssign || save.isPending}
              onClick={() =>
                save.mutate(
                  hasRole ? current.filter((id) => id !== role.id) : [...current, role.id],
                )
              }
            >
              {name}
            </Btn>
          );
        })}
      </div>
      {melding.element}
    </Sect>
  );
}
